import { useCallback, useMemo } from 'react';
import { Tag, useUserInputsApiFacade } from '../../../../../apis/facades/useUserInputsApiFacade';
import { useDispatch, useSelector } from 'react-redux';
import {
  addTag,
  updateTag,
  deleteTag,
  setTagConfigurations,
  clearTagConfigurations,
  deleteTagFromConfigurationUserInputs,
} from '../../../../../reducers/documentation/userInputsReducer';
import { telemetry } from '../../../../../telemetry';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { TagType } from '../../header/TagsHeader';
import { UserInputTypes } from '@server/user-inputs';
import { selectAccountUsers } from '../../../../../reducers/accountUsersReducer';
import {
  openEditTag,
  selectParsedConfigurationItems,
} from '../../../../../reducers/documentation/documentationReducer';
import { selectEnvironments } from '../../../../../reducers/global/globalReducer';
import {
  selectLayoutsByObjectName,
  selectParsedFields,
  selectParsedRecordTypes,
} from '../../../../../reducers/global/parserReducer';
import { selectCrmOrgRollups } from '../../../../../reducers/global/rollupReducers';
import { getOptimizedConfigurationItem } from '../../hooks/useGetConfigurationItem';
import { parseIdToValueAndNameToLabel } from './utils';
import groupBy from 'lodash/groupBy';
import { ConfigurationItemMap } from '../../../configuration-item-tables/shared/helpers';
import { useGetFilteredParsedRules } from '../../../selected-object/filters/useGetFilteredParsedRules';
import { InlineAutocompleteItem } from '../../../../common/InlineAutocomplete/InlineAutocomplete';
import { selectTagsList } from '../../../../../reducers/documentation/userInputsReducer';
import { useDocumentationContext } from '../../../context/DocumentationContext';

export const useTags = (crmOrgId: string) => {
  const dispatch = useDispatch();
  const {
    post_tag,
    patch_tag,
    delete_tag,
    get_tagConfigurations,
    delete_userConfigurations,
    get_userConfigurations,
  } = useUserInputsApiFacade();
  const { addNotification } = useSweepNotifications();
  const { recordTypesUsedInCanvasByObjectName: recordTypeNamesUsedInCanvas } =
    useDocumentationContext();

  const objectLayouts = useSelector(selectLayoutsByObjectName(crmOrgId));
  const parsedConfigurationItems = useSelector(selectParsedConfigurationItems(crmOrgId));
  const parsedFields = useSelector(selectParsedFields(crmOrgId));
  const rollups = useSelector(selectCrmOrgRollups(crmOrgId));
  const globalEnvironments = useSelector(selectEnvironments);
  const tags = useSelector(selectTagsList);
  const users = useSelector(selectAccountUsers);
  const _parsedUsers = parseIdToValueAndNameToLabel(users ?? []);
  const parsedRecordTypes = useSelector(selectParsedRecordTypes(crmOrgId));
  const parsedRules = useGetFilteredParsedRules(crmOrgId);

  const { automations, alerts, matching, dedup, assignments, scheduledAssignments, sweepFields } =
    globalEnvironments?.[crmOrgId]?.data ?? {};

  const indexedLookups = useMemo(() => {
    const tagsById = new Map(tags?.map((tag) => [tag.id, tag]) ?? []);
    const usersById = new Map(_parsedUsers?.map((user) => [user.value, user]) ?? []);
    const layoutsById = new Map<string, any>();
    const layoutsByName = new Map<string, any>();
    const allLayoutsArray = Object.values(objectLayouts ?? {})
      .map((layouts) => layouts.layouts)
      .flat();

    allLayoutsArray?.forEach((layout) => {
      if (layout.id) layoutsById.set(layout.id, layout);
      if (layout.name) layoutsByName.set(layout.name, layout);
    });

    const rulesById = new Map<string, any>();
    const rulesByName = new Map<string, any[]>();
    parsedRules?.forEach((rule) => {
      if (rule.id) rulesById.set(rule.id, rule);
      if (rule.name) {
        const existingRules = rulesByName.get(rule.name) ?? [];
        rulesByName.set(rule.name, [...existingRules, rule]);
      }
    });

    const rollupsById = new Map<string, any>();
    const rollupsByFieldId = new Map<string, any>();
    rollups?.forEach((rollup) => {
      if (rollup.rollupId) rollupsById.set(rollup.rollupId, rollup);
      if (rollup.rollupField?.id) rollupsByFieldId.set(rollup.rollupField.id, rollup);
    });

    const recordTypesByName = new Map<string, any>();
    const recordTypesByKey = new Map<string, any>();
    parsedRecordTypes?.forEach((rt) => {
      if (rt.name) recordTypesByName.set(`${rt.name}-${rt.objectApiName}`, rt);
    });

    const sweepFieldsById = new Map(Object.entries(sweepFields ?? {}));

    return {
      tagsById,
      usersById,
      layoutsById,
      layoutsByName,
      rulesById,
      rulesByName,
      rollupsById,
      rollupsByFieldId,
      recordTypesByName,
      recordTypesByKey,
      sweepFieldsById,
      allLayoutsArray,
    };
  }, [tags, _parsedUsers, objectLayouts, parsedRules, rollups, parsedRecordTypes, sweepFields]);

  const cachedExpensiveOperations = useMemo(() => {
    const makeNotUndefined = (items?: AutomationStructureNew[]) => items ?? [];

    const mergedAutomations = groupBy(
      [
        ...makeNotUndefined(automations),
        ...makeNotUndefined(alerts),
        ...makeNotUndefined(assignments),
        ...makeNotUndefined(matching),
        ...makeNotUndefined(dedup),
        ...makeNotUndefined(scheduledAssignments),
      ].flat(),
      'automationId',
    );

    const configItemsById = new Map<string, any>();
    const configItemsByName = new Map<string, any[]>();
    parsedConfigurationItems?.forEach((item) => {
      if (item.id) configItemsById.set(item.id, item);
      if (item.name) {
        const existingItems = configItemsByName.get(item.name) ?? [];
        configItemsByName.set(item.name, [...existingItems, item]);
      }
    });

    const allFieldsFlattened: Array<any> = [];
    const fieldsById = new Map<string, any>();

    if (parsedFields) {
      Object.keys(parsedFields).forEach((objectName) => {
        parsedFields[objectName]?.forEach((field) => {
          const fieldWithObject = { ...field, objectName };
          allFieldsFlattened.push(fieldWithObject);
          if (field.id) fieldsById.set(field.id, fieldWithObject);
        });
      });
    }

    const elementsById = new Map();
    const elementsByName = new Map();
    const metadataElementsById = new Map();

    return {
      mergedAutomations,
      configItemsById,
      configItemsByName,
      allFieldsFlattened,
      fieldsById,
      elementsById,
      elementsByName,
      metadataElementsById,
    };
  }, [
    automations,
    alerts,
    assignments,
    matching,
    dedup,
    scheduledAssignments,
    parsedConfigurationItems,
    parsedFields,
  ]);

  const clearTagOrUserConfigurations = useCallback(() => {
    dispatch(clearTagConfigurations());
  }, [dispatch]);

  const _getConfigurationItem = useCallback(
    (configurationId: string) => {
      return getOptimizedConfigurationItem({
        searchCriteria: {
          id: configurationId,
          searchByName: false,
        },
        recordTypeNamesUsedInCanvas: recordTypeNamesUsedInCanvas ?? {},
        parsedFields,
        users,
        indexedLookups,
        cachedExpensiveOperations,
      });
    },
    [recordTypeNamesUsedInCanvas, parsedFields, users, indexedLookups, cachedExpensiveOperations],
  );

  const createConfigurationItemMap = useCallback(
    (response: string[]) => {
      return groupBy(
        response.map((itemId) => _getConfigurationItem(itemId)).filter((item) => !!item),
        'parentType',
      ) as ConfigurationItemMap;
    },
    [_getConfigurationItem],
  );

  const fetchTagConfigurations = useCallback(
    async (tagId: string, crmOrgId: string) => {
      try {
        const response = await get_tagConfigurations(tagId, crmOrgId);
        dispatch(
          setTagConfigurations({
            configurations: {
              [UserInputTypes.Tags]: createConfigurationItemMap(response),
              [UserInputTypes.Owner]: createConfigurationItemMap([]),
              [UserInputTypes.Collaborators]: createConfigurationItemMap([]),
            },
            tagId,
          }),
        );
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [get_tagConfigurations, dispatch, createConfigurationItemMap],
  );

  const fetchUserConfigurations = useCallback(
    async (tagId: string, crmOrgId: string) => {
      try {
        const response = await get_userConfigurations(tagId, crmOrgId);
        dispatch(
          setTagConfigurations({
            configurations: {
              [UserInputTypes.Tags]: createConfigurationItemMap([]),
              [UserInputTypes.Owner]: createConfigurationItemMap(response[UserInputTypes.Owner]),
              [UserInputTypes.Collaborators]: createConfigurationItemMap(
                response[UserInputTypes.Collaborators],
              ),
            },
            tagId,
          }),
        );
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [get_userConfigurations, dispatch, createConfigurationItemMap],
  );

  const onAddTag = useCallback(
    async (tag: Omit<Tag, 'id'>) => {
      try {
        const tagId = await post_tag(tag);
        dispatch(addTag({ tag: { ...tag, id: tagId } }));
        return tagId;
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [post_tag, dispatch],
  );

  const onUpdateTag = useCallback(
    async (tag: Tag) => {
      try {
        dispatch(updateTag({ tag }));
        await patch_tag(tag);
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [patch_tag, dispatch],
  );

  const onDeleteTag = useCallback(
    async (tagId: string, tagType: TagType) => {
      try {
        if (tagType === TagType.tag) {
          await delete_tag(tagId);
        } else {
          await delete_userConfigurations(tagId);
        }
        dispatch(deleteTag({ tagId }));
        dispatch(deleteTagFromConfigurationUserInputs({ tagId, crmOrgId }));

        const message =
          tagType === TagType.tag ? `Tag successfully removed` : 'User successfully unlinked';

        addNotification({
          message,
          variant: SweepNotificationVariant.Success,
        });
        return true;
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: `Failed to remove due to technical issues`,
          keepOpen: true,
          variant: SweepNotificationVariant.Error,
        });
        return false;
      }
    },
    [delete_tag, crmOrgId, delete_userConfigurations, addNotification, dispatch],
  );

  const onTagClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      tag: InlineAutocompleteItem,
      clearHistory?: boolean,
    ) => {
      dispatch(openEditTag({ tagId: tag.value, tagName: tag.label, clearHistory }));
    },
    [dispatch],
  );

  return {
    onAddTag,
    onDeleteTag,
    onUpdateTag,
    fetchTagConfigurations,
    fetchUserConfigurations,
    clearTagOrUserConfigurations,
    onTagClick,
  };
};
